import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BookOpen, Repeat2, ArrowLeftRight, Shuffle, Hash, Layers, ArrowLeft } from "lucide-react";
import PageHeader from "../components/PageHeader";
import "../styles/vocabTestSetup.css";

const TYPE_META = {
  definitions: { label: "Definitions Test", icon: <BookOpen size={20} />, color: "vts-indigo" },
  synonyms:    { label: "Synonyms Test",    icon: <Repeat2 size={20} />,      color: "vts-teal"   },
  antonyms:    { label: "Antonyms Test",    icon: <ArrowLeftRight size={20} />, color: "vts-orange" },
  mixed:       { label: "Mixed Test",       icon: <Shuffle size={20} />,       color: "vts-purple" },
};

const WORD_COUNTS = [10, 25, 50, 100];

const TIER_OPTIONS = [
  { id: "1-4",  startTier: 1,  endTier: 4,   label: "Tiers 1–4 (Beginner)" },
  { id: "5-9",  startTier: 5,  endTier: 9,   label: "Tiers 5–9 (Intermediate)" },
  { id: "10-14",startTier: 10, endTier: 14,  label: "Tiers 10–14 (Advanced)" },
  { id: "15-19",startTier: 15, endTier: 19,  label: "Tiers 15–19 (Expert)" },
  { id: "20+",  startTier: 20, endTier: null, label: "Tier 20+ (Master)" },
  { id: "all",  startTier: 1,  endTier: null, label: "All tiers (Tier 1+)" },
];

export default function VocabTestSetup() {
  const navigate = useNavigate();
  const location = useLocation();
  const testType = location.state?.testType || "definitions";
  const meta = TYPE_META[testType] || TYPE_META.definitions;

  useEffect(() => { document.title = "Test Setup — Owllet Reading Quest"; }, []);

  const [wordCount, setWordCount] = useState(10);
  const [tierId, setTierId] = useState("1-4");

  const handleStart = () => {
    const tierOpt = TIER_OPTIONS.find((o) => o.id === tierId) || TIER_OPTIONS[0];
    navigate("/vocab-test/game", {
      state: { testType, wordCount, startTier: tierOpt.startTier, endTier: tierOpt.endTier },
    });
  };

  return (
    <div className="vts-page">
      <PageHeader pageTitle="Test Setup" pageSubtitle="Configure your vocabulary test" />

      <div className="vts-body">
        {/* Test type badge */}
        <div className={`vts-type-badge ${meta.color}`}>
          {meta.icon}
          <span>{meta.label}</span>
        </div>

        {/* Word count */}
        <section className="vts-section">
          <div className="vts-section-header">
            <Hash size={18} />
            <h2>Number of questions</h2>
          </div>
          <div className="vts-options">
            {WORD_COUNTS.map((n) => (
              <button
                key={n}
                className={`vts-option-btn ${wordCount === n ? "active" : ""}`}
                onClick={() => setWordCount(n)}
              >
                {n}
              </button>
            ))}
          </div>
        </section>

        {/* Starting tier */}
        <section className="vts-section">
          <div className="vts-section-header">
            <Layers size={18} />
            <h2>Starting tier</h2>
          </div>
          <div className="vts-tier-options">
            {TIER_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                className={`vts-tier-btn ${tierId === opt.id ? "active" : ""}`}
                onClick={() => setTierId(opt.id)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </section>

        {/* Info note */}
        <p className="vts-note">
          Questions are weighted toward your weakest words so you improve faster.
        </p>

        {/* Actions */}
        <div className="vts-actions">
          <button className="vts-back-btn" onClick={() => navigate("/test-modes")}>
            <ArrowLeft size={15} /> Back
          </button>
          <button className="vts-start-btn" onClick={handleStart}>
            Start Test →
          </button>
        </div>
      </div>
    </div>
  );
}
